export const DOCUSIGN_CONNECTION_STATUS = {
  CONNECTED: "connected",
  NOT_CONNECTED: "not_connected",
  CONSENT_REQUIRED: "consent_required",
} as const;

export type DocusignConnectionStatus =
  (typeof DOCUSIGN_CONNECTION_STATUS)[keyof typeof DOCUSIGN_CONNECTION_STATUS];

export const DOCUSIGN_ERROR_CODES = {
  CREDENTIALS_NOT_FOUND: "DOCUSIGN_ORG_CREDENTIALS_NOT_FOUND",
  TEMPLATE_NOT_FOUND: "DOCUSIGN_ORG_TEMPLATE_NOT_FOUND",
  DUPLICATE_TEMPLATE: "DOCUSIGN_ORG_DUPLICATE_TEMPLATE",
  UNAVAILABLE: "DOCUSIGN_ORG_UNAVAILABLE",
} as const;

export type DocusignErrorCode =
  (typeof DOCUSIGN_ERROR_CODES)[keyof typeof DOCUSIGN_ERROR_CODES];

export const DOCUSIGN_TEMPLATE_ERROR_MAP: Record<string, string> = {
  [DOCUSIGN_ERROR_CODES.CREDENTIALS_NOT_FOUND]:
    "Docusign credentials are not set up. Connect Docusign before linking templates.",
  [DOCUSIGN_ERROR_CODES.TEMPLATE_NOT_FOUND]:
    "Template not found in your Docusign account.",
  [DOCUSIGN_ERROR_CODES.DUPLICATE_TEMPLATE]:
    "This template is already linked.",
  [DOCUSIGN_ERROR_CODES.UNAVAILABLE]:
    "Docusign is temporarily unavailable. Please try again.",
};

export const DOCUSIGN_DELETE_CONFIRMATION =
  "This will disconnect Docusign and remove all linked templates. Continue?";

// Query params the BE consent callback appends when redirecting back to
// /organization-settings (`?docusign=connected|error&reason=<...>`).
// Contract shared with tcc-api (DocusignConnectQueryParameter/-ErrorReason).
export const DOCUSIGN_CALLBACK_PARAM = "docusign";
export const DOCUSIGN_CALLBACK_REASON_PARAM = "reason";

const DOCUSIGN_CALLBACK_RESULT = {
  CONNECTED: "connected",
  ERROR: "error",
} as const;

const DOCUSIGN_CALLBACK_ERROR_MESSAGES: Record<string, string> = {
  consent_denied:
    "Consent was declined in Docusign. Try again and choose Allow Access.",
  state_mismatch:
    "The connection link was invalid or expired. Please start the connection again.",
};

const DOCUSIGN_CALLBACK_GENERIC_ERROR_MESSAGE =
  "We couldn't complete the Docusign connection. Please try again.";

export interface DocusignCallbackToast {
  title: string;
  description: string;
  variant?: "destructive";
}

export const getDocusignCallbackToast = (
  params: URLSearchParams
): DocusignCallbackToast | null => {
  const result = params.get(DOCUSIGN_CALLBACK_PARAM);

  if (result === DOCUSIGN_CALLBACK_RESULT.CONNECTED) {
    return {
      title: "Docusign Connected",
      description: "Your organization is now connected to Docusign.",
    };
  }

  if (result === DOCUSIGN_CALLBACK_RESULT.ERROR) {
    const reason = params.get(DOCUSIGN_CALLBACK_REASON_PARAM) ?? "";
    return {
      title: "Docusign Not Connected",
      description:
        DOCUSIGN_CALLBACK_ERROR_MESSAGES[reason] ??
        DOCUSIGN_CALLBACK_GENERIC_ERROR_MESSAGE,
      variant: "destructive",
    };
  }

  return null;
};
